import { useCallback, useEffect, useMemo, useState } from 'react'
import { getOverflowDirections } from 'lib/util/scroll'
import throttle from 'lib/util/throttle'
import useResizeObserver from './useResizeObserver'

/**
 * The `useScrollOverflow` provides information on whether
 * a scroll container has overflow and of which side the
 * overflow is occurring
 *
 * @param {object} ref - the ref to the scrolling container DOM element
 * @param {number} threshold - the threshold (in pixels) in which to pick up overflow
 * @param {boolean} isEnabled - whether is enabled
 * @returns {object} the context
 */
function useScrollOverflow (ref, threshold = 20, isEnabled = true) {
  const [hasOverflow, setHasOverflow] = useState(false)
  const [hasOverflowTop, setHasOverflowTop] = useState(false)
  const [hasOverflowRight, setHasOverflowRight] = useState(false)
  const [hasOverflowBottom, setHasOverflowBottom] = useState(false)
  const [hasOverflowLeft, setHasOverflowLeft] = useState(false)

  /**
   * On refresh - called on resize observation or scroll. Using
   * throttle to reduce the amount of calls
   */
  const onRefresh = useCallback(
    throttle(() => {
      const scrollContainer = ref.current

      if (!scrollContainer) {
        return
      }

      const { bottom, left, right, top } = getOverflowDirections(
        scrollContainer,
        threshold
      )

      setHasOverflow(top || right || bottom || left)
      setHasOverflowTop(top)
      setHasOverflowRight(right)
      setHasOverflowBottom(bottom)
      setHasOverflowLeft(left)
    }, 50),
    []
  )

  // Add the resize observer
  useResizeObserver(ref, onRefresh)

  /**
   * Effect: On mount/unmount
   */
  useEffect(() => {
    if (!ref?.current) {
      // Defensive
      return
    }

    ref.current.addEventListener('scroll', onRefresh)
    onRefresh()

    return () => {
      if (!ref.current) {
        return
      }

      ref.current.removeEventListener('scroll', onRefresh)
    }
  }, [])

  const ctx = useMemo(
    () => ({
      hasOverflow,
      hasOverflowLeft,
      hasOverflowRight,
      hasOverflowTop,
      hasOverflowBottom
    }),
    [
      hasOverflow,
      hasOverflowLeft,
      hasOverflowRight,
      hasOverflowTop,
      hasOverflowBottom
    ]
  )

  return ctx
}

// Export
export default useScrollOverflow
